import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { PreviewThumbnail } from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import { PreviewThumbnailsComponent } from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnails.component';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { ToastService } from 'src/app/services/toast.service';

export const CachedMediaStore = signalStore(
  withState({
    _cache: {} as { [id: number]: PreviewThumbnail[] },
    hasUnsaved: false,
  }),
  withMethods(_ => ({
    _validatePreview(preview: PreviewThumbnailsComponent): preview is PreviewThumbnailsComponent {
      if (!preview) throw 'No preview Component';
      return true;
    },
  })),
  withMethods(
    (
      store,
      mediaService = inject(MediaApiFunc),
      base64service = inject(Base64Service),
      toastr = inject(ToastService)
    ) => ({
      getFromCache(id: number, preview: PreviewThumbnailsComponent) {
        store._validatePreview(preview);

        const cache = store._cache();
        preview.clearPreview();
        if (cache[id])
          setTimeout(() => {
            preview.rawPreviewThumbnails = [...cache[id]];
          });
      },

      async getBilder(id: number, bildpath: string, preview: PreviewThumbnailsComponent) {
        store._validatePreview(preview);

        try {
          const cache = store._cache();
          if (!cache[id] || cache[id].length == 0) {
            if (bildpath == undefined) {
              preview.unpackFiles([]);
              return;
            }
            const { fileNames } = mediaService.parseMediaPath(bildpath)[0] ?? {};
            const files = await mediaService.getApiFunctionMedia(fileNames);
            preview.unpackFiles(files, rawPreviews => {
              cache[id] = [...rawPreviews];
              patchState(store, { _cache: cache });
            });
          } else
            setTimeout(() => {
              preview.rawPreviewThumbnails = [...cache[id]];
            });
        } catch (er) {
          toastr.error((er as Error).message);
        }
      },

      reloadAfterSaved(id: number, preview: PreviewThumbnailsComponent, deletedFiles: string[]) {
        store._validatePreview(preview);

        const cache = store._cache();
        if (preview.unsaved().length) cache[id] = null;
        else if (deletedFiles.length) cache[id] = [...preview.rawPreviewThumbnails];
        patchState(store, { _cache: cache });
      },

      resetCache() {
        patchState(store, { _cache: {}, hasUnsaved: false });
      },

      setInCache(id: number, preview: PreviewThumbnailsComponent) {
        store._validatePreview(preview);

        const cache = store._cache();
        cache[id] = [...preview.rawPreviewThumbnails];
        preview.clearPreview();
        patchState(store, { _cache: cache, hasUnsaved: true });
      },

      deleteInCache(id: number, preview: PreviewThumbnailsComponent) {
        store._validatePreview(preview);

        const cache = store._cache();
        delete cache[id];
        preview.clearPreview();
        patchState(store, { _cache: cache, hasUnsaved: true });
      },

      packFilesFromCache(id: number) {
        const cache = store._cache();
        const previews = cache[id];
        return previews?.length ? base64service.packFiles(previews) : null;
      },

      setSavedInCache(id?: number) {
        const cache = store._cache();
        const previews = id ? cache[id] : Object.values(cache).flat();
        previews.forEach(thumbnail => (thumbnail.saved = true));

        patchState(store, { _cache: cache, hasUnsaved: false });
      },
    })
  ),
  withHooks({
    onInit: store => {
      store.resetCache();
    },
  })
);
